import { useCallback, useState, useRef } from 'react';
import {
  setEmail,
  setPassword,
} from 'src/components/authentication/store/authSlice';
import {
  cleanPassword,
  isValidPassword,
  PasswordFailureReason,
  getPasswordFailures,
} from 'src/util/credentialFormatValidation';
import { isValidEmail } from 'src/util/emailUtils';
import { EmailInput } from 'src/components/authentication/flow-pages/AuthenticationEmailInput';
import { PasswordInput } from 'src/components/authentication/flow-pages/AuthenticationPasswordInput';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'src/components/authentication/store';

export type AuthSubmitInputs = EmailInput | PasswordInput;

export type AttemptSubmitSettings = {
  onSubmit: () => Promise<void> | void;
  userCredentialsUpdated: {
    [K in AuthSubmitInputs]: string;
  };
  requireBothInputs?: boolean;
};

export const useAuthSubmit = () => {
  const dispatch = useDispatch();
  const startingEmail = useSelector((state: RootState) => {
    return state.email;
  });
  const [userCredentials, setUserCredentials] = useState<{
    [K in AuthSubmitInputs]: string;
  }>({
    email: startingEmail,
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<{
    [K in AuthSubmitInputs]: boolean;
  }>({
    email: false,
    password: false,
    confirmPassword: false,
  });

  const [passwordStrengthErrors, setPasswordStrengthErrors] = useState<{
    [K in PasswordFailureReason]: boolean;
  }>({
    'blacklisted char': false,
    'needs lowercase': false,
    'needs uppercase': false,
    'needs number': false,
    'needs symbol': false,
    'too short': false,
    'too long': false,
  });
  const confirmPasswordsMatch = () => {
    return userCredentials.password === userCredentials.confirmPassword;
  };
  const updatePasswordErrors = (passwordFailures: PasswordFailureReason[]) => {
    const newPasswordErrors: { [K in PasswordFailureReason]?: boolean } = {};
    (Object.keys(passwordStrengthErrors) as PasswordFailureReason[]).forEach(
      (missingFailureReason: PasswordFailureReason) => {
        newPasswordErrors[`${missingFailureReason}`] = false;
      },
    );
    passwordFailures.forEach((failureReason: PasswordFailureReason) => {
      newPasswordErrors[`${failureReason}`] = true;
    });
    setPasswordStrengthErrors({
      ...passwordStrengthErrors,
      ...newPasswordErrors,
    });
  };

  const userHasTypedInPassBox = useRef(false);
  const onInputChange = (
    inputName: AuthSubmitInputs,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setUserCredentials({
      ...userCredentials,
      [inputName]: event.currentTarget.value,
    });
    if (inputName !== 'email' && !userHasTypedInPassBox.current) {
      userHasTypedInPassBox.current = true;
    }
    if (inputName !== 'confirmPassword') {
      setErrors({
        ...errors,
        [inputName]: false,
      });

      if (inputName === 'password') {
        updatePasswordErrors(getPasswordFailures(event.currentTarget.value));
      }
    } else {
      setErrors({
        ...errors,
        [inputName]: confirmPasswordsMatch(),
      });
    }
  };

  const attemptSubmit = useCallback(
    async ({
      onSubmit,
      requireBothInputs = true,
      userCredentialsUpdated,
    }: AttemptSubmitSettings) => {
      const cleanedEmail = userCredentialsUpdated.email.toLowerCase();
      const validEmail = isValidEmail(cleanedEmail);
      const cleanedPassword = cleanPassword(userCredentialsUpdated.password);
      const validPassword = isValidPassword(cleanedPassword);

      if (
        (!validEmail && requireBothInputs) ||
        !validPassword ||
        !confirmPasswordsMatch()
      ) {
        const updatedErrors = { ...errors };
        if (!validEmail && requireBothInputs) {
          updatedErrors.email = true;
        }
        if (!validPassword) {
          const passwordFailures: PasswordFailureReason[] = getPasswordFailures(
            userCredentialsUpdated.password,
          );
          updatePasswordErrors(passwordFailures);
          updatedErrors.password = true;
        }
        if (!confirmPasswordsMatch()) {
          updatedErrors.confirmPassword = true;
        }
        setErrors(updatedErrors);
      } else {
        setErrors({
          ...errors,
          email: false,
          password: false,
          confirmPassword: false,
        });
        try {
          await onSubmit();
        } catch (e) {
          setErrors({ ...errors, password: true });
          console.error(e);
        }
        if (requireBothInputs) {
          dispatch(setEmail(cleanedEmail));
        }
        dispatch(setPassword(cleanedPassword));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, errors],
  );

  return {
    attemptSubmit,
    passwordStrengthErrors,
    userCredentials,
    setUserCredentials,
    errors,
    onInputChange,
    userHasTypedInPassBox,
  };
};
